import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { ref, push, set } from 'firebase/database';
import SEO from '../components/SEO';

const companyName = process.env.REACT_APP_COMPANY_NAME || 'Autopilot';
const companyEmail = process.env.REACT_APP_COMPANY_EMAIL || 'support@autopilot.co.zw';
const companyPhone = process.env.REACT_APP_COMPANY_PHONE || '+263 78 123 4567';
const companyAddress = process.env.REACT_APP_COMPANY_ADDRESS || 'Harare, Zimbabwe';
const API_BASE = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || '');

const subjects = [
  { value: 'general',     label: 'General enquiry' },
  { value: 'sales',       label: 'Sales / pricing' },
  { value: 'support',     label: 'Technical support' },
  { value: 'billing',     label: 'Billing question' },
  { value: 'partnership', label: 'Partnership' },
];

const channels = [
  {
    icon: '📧',
    label: 'Email',
    value: companyEmail,
    href: `mailto:${companyEmail}`,
    note: 'We reply within 24 hours',
  },
  {
    icon: '📞',
    label: 'Phone / WhatsApp',
    value: companyPhone,
    href: `https://wa.me/${companyPhone.replace(/\D/g, '')}`,
    note: 'Mon–Fri, 8am–5pm CAT',
  },
  {
    icon: '📍',
    label: 'Location',
    value: companyAddress,
    href: '#',
    note: 'Harare, Zimbabwe',
  },
];

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 14px',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px',
  color: 'white',
  fontSize: '14px',
  fontFamily: 'inherit',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  boxSizing: 'border-box',
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '600',
  color: 'rgba(255,255,255,0.6)',
  marginBottom: '7px',
};

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', subject: 'general', message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { name, email, subject, message } = formData;
    if (!name.trim() || !email.trim() || !message.trim()) {
      setError('Please fill in your name, email, and message.');
      return;
    }

    setLoading(true);
    setError('');

    const ticket = {
      name:      formData.name.trim(),
      email:     formData.email.trim(),
      phone:     formData.phone.trim(),
      subject:   formData.subject,
      message:   formData.message.trim(),
      source:    'contact_page',
      status:    'open',
      createdAt: Date.now(),
    };

    let saved = false;

    // 1 — Try Firebase
    try {
      const ticketRef = push(ref(db, 'contact_submissions'));
      await set(ticketRef, { id: ticketRef.key, ...ticket });
      saved = true;
    } catch (fbErr) {
      console.warn('Firebase write failed:', fbErr);
    }

    // 2 — Try backend API
    if (!saved && API_BASE) {
      try {
        const res = await fetch(`${API_BASE}/api/contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(ticket),
        });
        if (res.ok) saved = true;
      } catch (apiErr) {
        console.warn('API fallback failed:', apiErr);
      }
    }

    // 3 — mailto fallback (always works, opens email client)
    if (!saved) {
      const body = `Name: ${ticket.name}\nEmail: ${ticket.email}\nPhone: ${ticket.phone || 'N/A'}\nSubject: ${ticket.subject}\n\n${ticket.message}`;
      window.open(
        `mailto:${companyEmail}?subject=${encodeURIComponent(ticket.subject)}&body=${encodeURIComponent(body)}`,
        '_blank',
      );
    }

    setLoading(false);
    setSubmitted(true);
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', subject: 'general', message: '' });
    setSubmitted(false);
    setError('');
  };

  return (
    <>
      <SEO
        title={`Contact | ${companyName}`}
        description={`Get in touch with ${companyName}. Sales, support, or general questions — we're here to help.`}
        keywords={`contact ${companyName}, WhatsApp bot support, Zimbabwe tech support`}
        type="website"
      />

      <style>{`
        * { box-sizing: border-box; }
        .contact-grid { grid-template-columns: 1fr 400px; }
        .contact-hero { padding: 120px 24px 56px; }
        .contact-body { padding: 0 24px 80px; }
        @media (max-width: 900px) {
          .contact-grid { grid-template-columns: 1fr !important; }
          .contact-hero { padding: 96px 20px 44px; }
          .contact-body { padding: 0 20px 60px; }
        }
        input:focus, textarea:focus, select:focus {
          border-color: #5B4FE8 !important;
          box-shadow: 0 0 0 3px rgba(91,79,232,0.15);
        }
        input::placeholder, textarea::placeholder { color: rgba(255,255,255,0.2); }
        .contact-channel:hover { border-color: rgba(91,79,232,0.4) !important; }
      `}</style>

      <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", background: '#0a0914', color: 'white', minHeight: '100vh' }}>

        {/* ── NAV ── */}
        <nav style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
          padding: '0 32px', height: '60px',
          background: 'rgba(10,9,20,0.92)', backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Link to="/"><img src="/Logo-white.png" alt={companyName} style={{ height: '68px', display: 'block', objectFit: 'contain' }} /></Link>
          <div style={{ display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
            {[
              { to: '/',              label: 'Home' },
              { to: '/pricing' ,      label: 'Pricing' },
              { to: '/documentation', label: 'Docs' },
              { to: '/contact',       label: 'Contact' },
            ].map(({ to, label }) => (
              <Link key={to} to={to} style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '14px' }}>
                {label}
              </Link>
            ))}
            <Link to="/register" style={{ background: '#5B4FE8', color: 'white', padding: '8px 20px', borderRadius: '20px', textDecoration: 'none', fontSize: '14px', fontWeight: '600' }}>
              Get started
            </Link>
          </div>
        </nav>

        {/* ── HERO ── */}
        <div className="contact-hero" style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ display: 'inline-block', background: 'rgba(91,79,232,0.18)', border: '1px solid rgba(91,79,232,0.35)', color: '#a5b4fc', fontSize: '11px', fontWeight: '700', padding: '4px 14px', borderRadius: '20px', letterSpacing: '0.08em', marginBottom: '20px' }}>
            GET IN TOUCH
          </div>
          <h1 style={{ fontSize: 'clamp(32px,6vw,48px)', fontWeight: '800', letterSpacing: '-0.03em', lineHeight: 1.1, margin: '0 0 16px' }}>
            We'd love to<br />
            <span style={{ color: '#818cf8' }}>hear from you</span>
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '16px', lineHeight: 1.7, margin: 0 }}>
            Sales, support, or just curious — drop us a message and we'll get back to you quickly.
          </p>
        </div>

        {/* ── BODY ── */}
        <div className="contact-body" style={{ maxWidth: '1060px', margin: '0 auto' }}>
          <div className="contact-grid" style={{ display: 'grid', gap: '32px', alignItems: 'start' }}>

            {/* ── FORM ── */}
            <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '36px' }}>

              {submitted ? (
                /* ── Success state ── */
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{ fontSize: '52px', marginBottom: '16px' }}>✅</div>
                  <div style={{ fontWeight: '800', fontSize: '22px', marginBottom: '10px' }}>Message sent!</div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '15px', lineHeight: 1.6, marginBottom: '28px' }}>
                    Thanks for reaching out. We'll get back to you at <strong style={{ color: 'rgba(255,255,255,0.8)' }}>{formData.email}</strong> within 24 hours.
                  </div>
                  <button
                    onClick={resetForm}
                    style={{ padding: '11px 24px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: 'white', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: 'inherit' }}
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                /* ── Form ── */
                <form onSubmit={handleSubmit}>
                  <div style={{ fontWeight: '700', fontSize: '18px', marginBottom: '24px' }}>Send us a message</div>

                  {error && (
                    <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '11px 14px', marginBottom: '18px', color: '#fca5a5', fontSize: '13px' }}>
                      ⚠️ {error}
                    </div>
                  )}

                  {/* Name + Email */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <label style={labelStyle}>Name *</label>
                      <input type="text" required placeholder="Tino Moyo" value={formData.name}
                        onChange={e => setFormData({ ...formData, name: e.target.value })} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Email *</label>
                      <input type="email" required placeholder="tino@business.co.zw" value={formData.email}
                        onChange={e => setFormData({ ...formData, email: e.target.value })} style={inputStyle} />
                    </div>
                  </div>

                  {/* Phone + Subject */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <label style={labelStyle}>Phone / WhatsApp</label>
                      <input type="tel" placeholder="+263 77 123 4567" value={formData.phone}
                        onChange={e => setFormData({ ...formData, phone: e.target.value })} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Subject</label>
                      <select value={formData.subject}
                        onChange={e => setFormData({ ...formData, subject: e.target.value })}
                        style={{ ...inputStyle, background: '#1a1630', cursor: 'pointer', appearance: 'none' as const }}>
                        {subjects.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div style={{ marginBottom: '24px' }}>
                    <label style={labelStyle}>Message *</label>
                    <textarea
                      required
                      rows={5}
                      placeholder="Tell us how we can help…"
                      value={formData.message}
                      onChange={e => setFormData({ ...formData, message: e.target.value })}
                      style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.6' }}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      width: '100%', padding: '14px',
                      background: loading ? 'rgba(91,79,232,0.5)' : '#5B4FE8',
                      color: 'white', border: 'none', borderRadius: '12px',
                      fontWeight: '700', fontSize: '15px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit', transition: 'opacity 0.2s',
                    }}
                  >
                    {loading ? 'Sending…' : 'Send message →'}
                  </button>

                  <div style={{ marginTop: '14px', fontSize: '12px', color: 'rgba(255,255,255,0.3)', textAlign: 'center' }}>
                    We typically reply within a few hours during business hours.
                  </div>
                </form>
              )}
            </div>

            {/* ── SIDEBAR ── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* Contact channels */}
              {channels.map((c, i) => (
                <a
                  key={i}
                  href={c.href}
                  className="contact-channel"
                  target={c.href.startsWith('http') ? '_blank' : undefined}
                  rel="noreferrer"
                  style={{
                    display: 'flex', gap: '16px', alignItems: 'flex-start',
                    padding: '20px', background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px',
                    textDecoration: 'none', transition: 'border-color 0.2s',
                  }}
                >
                  <div style={{ width: '40px', height: '40px', background: 'rgba(91,79,232,0.15)', border: '1px solid rgba(91,79,232,0.25)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>
                    {c.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', fontWeight: '600', marginBottom: '3px' }}>{c.label}</div>
                    <div style={{ color: 'white', fontWeight: '600', fontSize: '14px', marginBottom: '2px' }}>{c.value}</div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)' }}>{c.note}</div>
                  </div>
                </a>
              ))}

              {/* Online badge */}
              <div style={{ padding: '18px 20px', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: '14px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#10b981', flexShrink: 0, boxShadow: '0 0 8px #10b981' }} />
                <div>
                  <div style={{ fontWeight: '700', fontSize: '13px', color: '#34d399', marginBottom: '2px' }}>We're here to help</div>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>Average response time: under 4 hours</div>
                </div>
              </div>

              {/* Quick links */}
              <div style={{ padding: '20px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '14px' }}>
                  Quick links
                </div>
                {[
                  { label: '📚 Documentation', to: '/documentation' },
                  { label: '❓ FAQ',            to: '/#faq' },
                  { label: '🆘 Support centre', to: '/support' },
                ].map((l, i, arr) => (
                  <Link
                    key={i}
                    to={l.to}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 0',
                      borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                      textDecoration: 'none', color: 'rgba(255,255,255,0.65)', fontSize: '14px',
                    }}
                  >
                    {l.label}
                    <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '16px' }}>›</span>
                  </Link>
                ))}
              </div>

            </div>
          </div>
        </div>

      </div>
    </>
  );
}